import React from "react"
import {
	Grid,
	Typography,
	FormControl,
	FormHelperText,
	Select,
	MenuItem,
	TextField,
	Button as MuiButton,
	Box
} from "@material-ui/core"
import {
	DescriptionOutlined as DescriptionIcon,
	KeyboardArrowDown as KeyboardArrowDownIcon
} from "@material-ui/icons"
import { SparklesIcon } from "lucide-react"

import { UserInInstance } from "@/protocols/userInInstance"

import useStyles from "@/pages/Admin/MeetingAnalyzer/CreateDialog/FormContent/styles"
import useValidation from "@/hooks/useValidation"

const MAX_TRANSCRIPTION_LENGTH = 80000
const TRANSCRIPTION_PLACEHOLDER = "Cole aqui a transcrição completa da reunião..."

type FormContentProps = {
	transcription: string
	selectedCloserId: number | null
	closers: UserInInstance[]
	useValidationOutput: ReturnType<typeof useValidation>
	isAnalyzing: boolean
	currentUserInInstanceId?: number
	onTranscriptionChange: (event: React.ChangeEvent<HTMLInputElement>) => void
	onCloserChange: (event: React.ChangeEvent<HTMLSelectElement>) => void
	onSubmit: () => void
}

const FormContent: React.FC<FormContentProps> = (props) => {
	const {
		transcription,
		selectedCloserId,
		closers,
		useValidationOutput,
		isAnalyzing,
		currentUserInInstanceId,
		onTranscriptionChange,
		onCloserChange,
		onSubmit
	} = props

	const classes = useStyles()

	const { validation } = useValidationOutput

	const isTranscriptionAtLimit = transcription.length >= MAX_TRANSCRIPTION_LENGTH
	const canSubmit = !isAnalyzing

	const renderCloserLabel = (closer: UserInInstance): string => {
		const isCurrentUser = closer.id === currentUserInInstanceId

		return isCurrentUser ? `${closer.name} (você)` : closer.name
	}

	return (
		<Grid container direction="column" spacing={3}>
			<Grid item>
				<Grid container spacing={2} alignItems="center">
					<Grid item>
						<Box className={classes.documentIconContainer}>
							<DescriptionIcon />
						</Box>
					</Grid>
					<Grid item>
						<Grid container direction="column">
							<Grid item>
								<Typography className={classes.sectionTitle}>
									Cole a transcrição da reunião abaixo
								</Typography>
							</Grid>
							<Grid item>
								<Typography className={classes.sectionSubtitle}>
									A IA irá analisar a conversa e devolver um diagnóstico completo com score, perfil e oportunidades de melhoria.
								</Typography>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Typography className={classes.closerFieldLabel}>
							Closer da reunião
						</Typography>
					</Grid>
					<Grid item>
						<FormControl variant="outlined" size="small" className={classes.closerSelectControl} error={Boolean(validation.userId)}>
							<Select
								value={selectedCloserId ?? ""}
								onChange={onCloserChange}
								displayEmpty
								IconComponent={KeyboardArrowDownIcon}
							>
								<MenuItem value="" disabled>
									Selecione o closer
								</MenuItem>
								{closers.map(closer => (
									<MenuItem key={closer.id} value={closer.id}>
										{renderCloserLabel(closer)}
									</MenuItem>
								))}
							</Select>
							{validation.userId && (
								<FormHelperText>{validation.userId}</FormHelperText>
							)}
						</FormControl>
					</Grid>
				</Grid>
			</Grid>

			<Grid item>
				<FormControl
					component={Grid}
					container
					variant="outlined"
					size="small"
					error={Boolean(validation.transcription)}
				>
					<TextField
						fullWidth
						multiline
						rows={10}
						placeholder={TRANSCRIPTION_PLACEHOLDER}
						value={transcription}
						onChange={onTranscriptionChange}
						variant="outlined"
						InputProps={{
							classes: {
								input: classes.transcriptionTextarea
							}
						}}
					/>
					{validation.transcription && (
						<FormHelperText>{validation.transcription}</FormHelperText>
					)}
				</FormControl>
			</Grid>

			<Grid item>
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid item>
						<Typography className={isTranscriptionAtLimit ? classes.counterTextAtLimit : classes.counterText}>
							{transcription.length} / 80.000 caracteres
						</Typography>
					</Grid>
					<Grid item>
						<MuiButton
							variant="contained"
							className={classes.saveButton}
							startIcon={<SparklesIcon size={18} />}
							disabled={!canSubmit}
							onClick={onSubmit}
						>
							Realizar análise da reunião
						</MuiButton>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	)
}

export default FormContent
